import {
  DispatchStatus,
  JobStatus,
  PermissionButton,
} from '@product-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import {
  FieldWrapperVertical,
  useMasterData,
  LoadingEvent,
} from '@ui-framework/index';
import { Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import ReadMoreText from '@ui-framework/components/UI/ReadMoreText';
import formatDateTime from '@product-framework/utils/formatDateTime';
import getClosestDateItemFromToday from '@ui-framework/helpers/getClosestDateItemFromToday';
import dayjs from 'dayjs';

function getFormattedClosestDate(jobInfo) {
  const resultDate = getClosestDateItemFromToday({
    dates: jobInfo?.customerServiceRequests,
    showDefaultDate: false,
    enablePastDates: true,
  });

  return resultDate?.requestDate && dayjs(resultDate?.requestDate).isValid()
    ? formatDateTime(
        resultDate?.requestDate,
        resultDate?.afterHour,
        resultDate?.beforeHour,
      )
    : resultDate;
}

function JobDetails({ jobInfo, jobLoading, setDialog }) {
  const { cancellableJobStatuses, reschedulableJobStatuses } = useMasterData();

  if (jobLoading) {
    return <LoadingEvent>processing...</LoadingEvent>;
  }

  const dateField =
    jobInfo?.serviceJobStatus === 'SERVICE_PENDING'
      ? {
          name: 'appointmentDate',
          label: {
            localeId: 'APPOINTMENT_DATE',
            defaultMessage: 'Appointment Date',
          },
          value: formatDateTime(
            jobInfo?.appointmentDate,
            jobInfo?.appointmentArrivalStartTime,
            jobInfo?.appointmentArrivalEndTime,
          ),
          grid: {
            md: 12,
          },
        }
      : {
          name: 'serviceDate',
          label: {
            localeId: 'SERVICE_REQUESTED_DATE',
            defaultMessage: 'Service Requested Dates',
          },
          value: getFormattedClosestDate(jobInfo),
          grid: {
            md: 12,
          },
        };

  const statusField =
    jobInfo?.serviceJobStatus === 'SERVICE_PENDING'
      ? {
          name: 'jobStatus',
          label: {
            localeId: 'JOB_STATUS',
            defaultMessage: 'Job Status',
          },
          value: (
            <JobStatus component="span">
              {jobInfo?.serviceJobStatus || ''}
            </JobStatus>
          ),
          grid: {
            md: 12,
          },
        }
      : {
          name: 'dispatchStatus',
          label: {
            localeId: 'DISPATCH_STATUS',
            defaultMessage: 'Dispatch Status',
          },
          value: (
            <DispatchStatus
              showAsText
              showAsTextProps={{ sx: { fontWeight: 'bold' } }}
              jobId={jobInfo?.id}
              component="span"
            />
          ),
          grid: {
            md: 12,
          },
        };

  const items = [
    dateField,
    {
      name: 'client',
      label: {
        localeId: 'CLIENT',
        defaultMessage: 'Client',
      },
      value: jobInfo?.serviceAdministratorClient?.clientName,
      grid: {
        md: 6,
      },
    },
    {
      name: 'brandProductType',
      label: {
        localeId: 'BRAND_AND_PRODUCT_TYPE',
        defaultMessage: 'Brand/Product Type',
      },
      value: [
        jobInfo?.brand?.brandName,
        jobInfo?.productType?.productLineName,
      ].join(' '),
      grid: {
        md: 6,
      },
    },
    {
      name: 'customerName',
      label: {
        localeId: 'CUSTOMER_NAME',
        defaultMessage: 'Customer Name',
      },
      value: [jobInfo?.customerNameFirst, jobInfo?.customerNameLast]
        .filter(Boolean)
        .join(' '),
      grid: {
        md: 6,
      },
    },
    {
      name: 'customerAddress',
      label: {
        localeId: 'CUSTOMER_ADDRESS',
        defaultMessage: 'Customer Address',
      },
      value: (
        <>
          {`${jobInfo?.customerAddressStreet}`}
          <br />
          {`${jobInfo?.customerAddressCity}, ${jobInfo?.customerAddressState?.abbreviation} ${jobInfo?.customerAddressPostalCode}`}
        </>
      ),
      grid: {
        md: 6,
      },
    },
    {
      name: 'problem',
      label: {
        localeId: 'PROBLEM',
        defaultMessage: 'Problem',
      },
      value: <ReadMoreText>{jobInfo?.customerComplaint}</ReadMoreText>,
      grid: {
        md: 6,
      },
    },
    {
      name: 'warrantyType',
      label: {
        localeId: 'WARRANTY_TYPE',
        defaultMessage: 'Warranty Type',
      },
      value: jobInfo?.warrantyTypeCode?.warrantyType,
      grid: {
        md: 6,
      },
    },
    statusField,
  ];

  return (
    <>
      <Grid2 container spacing={1}>
        {items.map(item => (
          <Grid2 key={item.name} md={item.grid.md}>
            <FieldWrapperVertical field={item}>
              {['jobStatus', 'dispatchStatus'].includes(item.name) ? (
                item.value
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {item.value}
                </Typography>
              )}
            </FieldWrapperVertical>
          </Grid2>
        ))}
      </Grid2>
      <Grid2
        container
        alignItems="center"
        sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
        <PermissionButton
          permissionName="CAN_UPDATE_JOB_FROM_MAP"
          sx={{ textTransform: 'none' }}
          variant="contained"
          disabled={!cancellableJobStatuses.includes(jobInfo?.serviceJobStatus)}
          onClick={() => setDialog('CANCEL')}>
          <FormattedMessage id="BUTTON_CANCEL" defaultMessage="Cancel" />
        </PermissionButton>
        <PermissionButton
          permissionName="CAN_UPDATE_JOB_FROM_MAP"
          sx={{ textTransform: 'none' }}
          variant="contained"
          disabled={
            !reschedulableJobStatuses.includes(jobInfo?.serviceJobStatus)
          }
          onClick={() => setDialog('RESCHEDULE')}>
          <FormattedMessage
            id="BUTTON_RESCHEDULE"
            defaultMessage="Reschedule"
          />
        </PermissionButton>
      </Grid2>
    </>
  );
}

export default JobDetails;
